import { Modifier } from './Modifier';

const elementSeparator = '__';
const modifierSeparator = '--' as const;

const createModifierString = (modifier: string | ((className: string) => string), className: string) => {
  if (!modifier) {
    return '';
  }

  if (typeof modifier === 'function') {
    return modifier(className);
  }

  if (modifier.includes(className)) {
    return modifier;
  }

  return `${className}${modifierSeparator}${modifier}`;
};

/**
 * Gets a className and it's BEM-Modifiers and returns a String which contains both
 * @param className {string} ClassName which will be extended
 * @param modifier {string|array} String or Array
 * @param withoutClassName {boolean}
 * @returns {string} Extended ClassName
 */
function extendClassName<TClassName extends string, TVal extends string, TModifier extends Modifier<TVal>>(
  className: TClassName,
  modifier?: TModifier,
  withoutClassName: boolean | undefined = undefined,
): string {
  let extension = null;
  if (modifier) {
    if (Array.isArray(modifier) && modifier.length > 0) {
      extension = modifier
        .filter(element => element !== null && element !== undefined)
        .map(m => createModifierString(m, className))
        .join(' ');
    } else if ((typeof modifier === 'string' || typeof modifier === 'function') && modifier !== '') {
      extension = createModifierString(modifier, className);
    }

    if (!Array.isArray(modifier) && withoutClassName) {
      return modifier;
    }
  }

  if (extension) {
    return `${className} ${extension}`;
  }

  return className;
}

export const createExtender =
  (blockName: string) =>
    <TVal extends string, TModifier extends Modifier<TVal>>(modifier: TModifier, withoutClassName = false) =>
      extendClassName(blockName, modifier, withoutClassName);

export const createBlock = (blockName: string) => ({
  className: blockName,
  createElement: (elementName: string) => ({
    className: `${blockName}${elementSeparator}${elementName}`,
    extender: createExtender(`${blockName}${elementSeparator}${elementName}`),
  }),
  extender: createExtender(blockName),
});

extendClassName.displayName = 'extendClassName';

export default extendClassName;
